import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Music, Play, Pause, Coins, Share2, Sparkles } from "lucide-react";
import { publicLoops, playedLoop, tipLoop, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Footer from "@/components/Footer";
import { LOOP_WALL } from "@/constants/testIds";

const NOTES = [261.63, 293.66, 329.63, 349.23, 392, 440, 493.88, 523.25];

function useSharedAudio() {
  const ctxRef = useRef(null);
  const get = () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
    }
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  };
  return get;
}

function LoopMiniViz({ notes }) {
  return (
    <div className="w-full h-16 flex items-end gap-0.5 p-2 rounded-lg bg-black/60 border border-[#00F0FF]/25 overflow-hidden">
      {Array.from({ length: 32 }).map((_, i) => {
        const bucket = notes.filter((n) => Math.floor((n.t / 4000) * 32) === i).length;
        const h = 4 + Math.min(56, bucket * 22);
        const opacity = 0.25 + Math.min(0.75, bucket * 0.25);
        return <div key={i} className="flex-1 rounded-sm" style={{ background: "#00F0FF", height: h, opacity }} />;
      })}
    </div>
  );
}

function LoopCard({ loop, onTip }) {
  const [playing, setPlaying] = useState(false);
  const getCtx = useSharedAudio();
  const timerRef = useRef(null);

  const play = () => {
    if (playing) return;
    setPlaying(true);
    const ctx = getCtx();
    (loop.notes || []).forEach((n) => {
      timerRef.current = setTimeout(() => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sawtooth";
        o.frequency.value = NOTES[n.idx] || 440;
        const f = ctx.createBiquadFilter();
        f.type = "lowpass"; f.frequency.value = 1400;
        o.connect(f); f.connect(g); g.connect(ctx.destination);
        const t = ctx.currentTime;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.14, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
        o.start(t); o.stop(t + 0.4);
      }, n.t);
    });
    setTimeout(() => setPlaying(false), 4300);
    playedLoop(loop.id).catch(() => {});
  };

  const stop = () => {
    setPlaying(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const share = async () => {
    const url = `${window.location.origin}/loops#${loop.id}`;
    try {
      if (navigator.share) await navigator.share({ title: `Jixxeo loop · ${loop.name}`, url });
      else { await navigator.clipboard.writeText(url); toast.success("Loop link copied"); }
    } catch {}
  };

  return (
    <motion.div
      data-testid={LOOP_WALL.card}
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="glass rounded-2xl p-4 flex flex-col gap-3 relative overflow-hidden hover:border-[#00F0FF]/40 border border-white/10 transition-colors">
      <div className="flex items-center gap-2">
        <span className="w-9 h-9 rounded-full border-2 flex items-center justify-center font-heading font-black text-sm"
          style={{ background: `${loop.user_avatar || "#00F0FF"}22`, color: loop.user_avatar || "#00F0FF", borderColor: `${loop.user_avatar || "#00F0FF"}55` }}>
          {(loop.user_name || "?")[0].toUpperCase()}
        </span>
        <div className="min-w-0">
          <div className="font-heading font-bold text-sm truncate">{loop.name}</div>
          <div className="mono text-[9px] uppercase tracking-widest text-white/50 truncate">
            by {loop.user_name || "anon"} · {loop.bpm} BPM
          </div>
        </div>
        <button onClick={share} title="Share"
          className="ml-auto w-8 h-8 rounded-full border border-white/10 hover:border-[#39FF14]/50 hover:text-[#39FF14] flex items-center justify-center text-white/70">
          <Share2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <LoopMiniViz notes={loop.notes || []} />

      <div className="flex items-center gap-2">
        <button data-testid={LOOP_WALL.playBtn} onClick={playing ? stop : play}
          className="inline-flex items-center gap-1.5 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-full">
          {playing ? <Pause className="w-3.5 h-3.5" fill="#000" /> : <Play className="w-3.5 h-3.5" fill="#000" />}
          {playing ? "Stop" : "Play"}
        </button>
        <button data-testid={LOOP_WALL.tipBtn} onClick={() => onTip(loop)}
          className="inline-flex items-center gap-1 border border-[#FFE300]/40 bg-[#FFE300]/10 text-[#FFE300] hover:bg-[#FFE300]/20 mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-full">
          <Coins className="w-3.5 h-3.5" /> Tip
        </button>
        <div className="ml-auto flex items-center gap-3 mono text-[10px] text-white/50">
          <span>{loop.plays || 0} plays</span>
          <span className="text-[#FFE300]">{loop.tips_received || 0}c</span>
        </div>
      </div>
    </motion.div>
  );
}

function TipLoopModal({ loop, onClose, onDone }) {
  const { user, setUser, refresh } = useAuth();
  const [amount, setAmount] = useState(10);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  if (!loop) return null;
  const QUICK = [5, 10, 25, 50, 100];

  const send = async () => {
    if (!user) return;
    if (amount < 1) return;
    if ((user.coins || 0) < amount) {
      toast.error("Not enough coins");
      return;
    }
    setBusy(true);
    try {
      const r = await tipLoop(loop.id, amount, msg);
      setUser({ ...user, coins: r.coins_remaining });
      toast.success(`Sent ${amount} coins to ${loop.user_name}`, { description: msg || "Nice loop 🎵" });
      onDone(amount);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); onClose(); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="glass-strong rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 border border-[#FFE300]/25"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mono text-[10px] uppercase tracking-widest text-[#FFE300] mb-1">// tip loop</div>
        <h3 className="font-heading font-black text-2xl uppercase tracking-tighter">Tip "{loop.name}"</h3>
        <div className="text-white/60 text-sm mt-1">to {loop.user_name}</div>

        <div className="mt-5 grid grid-cols-5 gap-2">
          {QUICK.map((q) => (
            <button key={q} onClick={() => setAmount(q)}
              className={`mono text-xs px-2 py-2 rounded-full border transition-colors ${amount === q ? "bg-[#FFE300] text-black border-[#FFE300]" : "border-white/10 text-white/80 hover:border-[#FFE300]/60"}`}>
              {q}
            </button>
          ))}
        </div>
        <input type="number" min="1" value={amount} onChange={(e) => setAmount(+e.target.value)}
          className="mt-3 w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#FFE300]/60" />
        <input value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={140} placeholder="Message (optional)"
          className="mt-2 w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#FFE300]/60" />

        <button onClick={send} disabled={busy}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#FFE300] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest py-3 rounded-full transition-colors disabled:opacity-50">
          <Coins className="w-4 h-4" /> {busy ? "Sending…" : `Tip ${amount} Coins`}
        </button>
        <button onClick={onClose} className="mt-2 w-full mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white py-2">
          Cancel
        </button>
      </motion.div>
    </div>
  );
}

export default function LoopWall() {
  const { user } = useAuth();
  const [loops, setLoops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipTarget, setTipTarget] = useState(null);

  useEffect(() => {
    publicLoops().then((d) => setLoops(d.loops || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid={LOOP_WALL.root} className="pt-8 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-11 h-11 rounded-xl bg-[#00F0FF] flex items-center justify-center shadow-[0_0_24px_rgba(0,240,255,0.5)]">
            <Music className="w-5 h-5 text-black" strokeWidth={3} />
          </span>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">// loop wall</div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter">Fresh Loops</h1>
          </div>
          <Link to="/app/jixxeo-vibes"
            className="ml-auto hidden sm:inline-flex items-center gap-1.5 bg-[#39FF14] hover:bg-white text-black font-bold mono text-[10px] uppercase tracking-widest px-4 py-2.5 rounded-full">
            <Sparkles className="w-3.5 h-3.5" /> Make a loop
          </Link>
        </div>
        <p className="text-white/60 max-w-xl mt-2">
          The freshest 4-second loops from Jixxeo Vibes creators. Tap to play, tip your favourites, and hear what the community's cooking.
        </p>

        {loading ? (
          <div className="mt-10 text-white/60 mono text-sm animate-pulse">Loading loops…</div>
        ) : loops.length === 0 ? (
          <div className="mt-10 glass rounded-2xl p-10 text-center">
            <div className="mono text-[10px] uppercase tracking-widest text-[#00F0FF] mb-2">// empty stage</div>
            <div className="text-white/70">
              Nobody's dropped a loop yet — <Link to="/app/jixxeo-vibes" className="text-[#00F0FF]">be first</Link>.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {loops.map((l) => (
              <LoopCard key={l.id} loop={l} onTip={(loop) => {
                if (!user) { toast.error("Sign in to tip"); return; }
                if (loop.user_id === user.id) { toast.error("You can't tip your own loop"); return; }
                setTipTarget(loop);
              }} />
            ))}
          </div>
        )}
      </div>
      <TipLoopModal loop={tipTarget} onClose={() => setTipTarget(null)}
        onDone={(amt) => {
          setLoops((prev) => prev.map((l) => l.id === tipTarget.id ? { ...l, tips_received: (l.tips_received || 0) + amt } : l));
        }} />
      <Footer />
    </div>
  );
}
